/**
 * Danh sách giao diện (visual_theme) dùng chung cho trang chi nhánh, trang liên hệ
 * và bảng chọn giao diện trong admin.
 *
 * Các giao diện cũ (tet, valentine, liberation, worldcup) đã được gỡ bỏ.
 * normalizeVisualTheme() đưa mọi giá trị lạ/cũ còn sót trong DB về 'default'.
 */

export const VISUAL_THEME_IDS = ['default', 'red', 'midautumn', 'halloween', 'christmas'] as const;

export type VisualThemeId = (typeof VISUAL_THEME_IDS)[number];

export interface VisualThemeOption {
  id: VisualThemeId;
  label: string;
  description: string;
  /** Giao diện ban đêm: luôn chạy nền tối, ẩn nút chuyển sáng/tối */
  nightOnly: boolean;
}

export const VISUAL_THEMES: VisualThemeOption[] = [
  {
    id: 'default',
    label: 'Giao diện mặc định',
    description: 'Giao diện hiện đại với gradient tối và hiệu ứng ánh sáng',
    nightOnly: false,
  },
  {
    id: 'red',
    label: 'Giao diện Đỏ',
    description: 'Giao diện rực rỡ, họa tiết hình khối giống trang chủ',
    nightOnly: false,
  },
  {
    id: 'midautumn',
    label: 'Giao diện Trung Thu',
    description: 'Trăng rằm, đèn ông sao và lồng đèn thả trời',
    nightOnly: true,
  },
  {
    id: 'halloween',
    label: 'Giao diện Halloween',
    description: 'Đêm ma quái với bí ngô, dơi bay và mạng nhện',
    nightOnly: true,
  },
  {
    id: 'christmas',
    label: 'Giao diện Giáng sinh',
    description: 'Thông Noel, dây đèn nhấp nháy và tuyết rơi',
    nightOnly: false,
  },
];

export function isVisualThemeId(value: unknown): value is VisualThemeId {
  return typeof value === 'string' && (VISUAL_THEME_IDS as readonly string[]).includes(value);
}

/** Đưa giá trị visual_theme trong DB về một id hợp lệ (giao diện đã gỡ -> 'default') */
export function normalizeVisualTheme(value?: string | null): VisualThemeId {
  return isVisualThemeId(value) ? value : 'default';
}

export function isNightOnlyTheme(theme: string): boolean {
  return VISUAL_THEMES.some((t) => t.id === theme && t.nightOnly);
}

/** Gradient cho icon mạng xã hội khi gradient_colors === 'theme' */
export const THEME_SOCIAL_GRADIENTS: Record<string, string> = {
  midautumn: 'linear-gradient(135deg,#B45309,#F59E0B,#FDE68A)',
  halloween: 'linear-gradient(135deg,#7C3AED,#F97316)',
  christmas: 'linear-gradient(135deg,#166534,#DC2626)',
  default: 'linear-gradient(135deg,#EE4D2D,#FF6533)',
};

export function getThemeSocialGradient(theme: string): string {
  return THEME_SOCIAL_GRADIENTS[theme] ?? THEME_SOCIAL_GRADIENTS.default;
}

/** Bộ class cho nút bấm trên trang chi nhánh của từng giao diện lễ hội */
export interface ThemeButtonStyle {
  plain: string;
  gradient: string;
}

export const THEME_BUTTON_STYLES: Record<string, ThemeButtonStyle> = {
  midautumn: {
    plain:
      'bg-[#141C3A]/75 backdrop-blur-md border border-amber-300/30 text-amber-50 hover:border-amber-200/70 hover:bg-[#1D2A55]/85',
    gradient:
      'bg-gradient-to-r from-[#B45309] via-[#F59E0B] to-[#FDE68A] text-[#2A1704] font-bold hover:shadow-[0_0_22px_rgba(245,158,11,0.45)]',
  },
  halloween: {
    plain:
      'bg-[#140B1F]/80 backdrop-blur-md border border-orange-400/35 text-orange-50 hover:border-orange-300/75 hover:bg-[#241134]/90',
    gradient:
      'bg-gradient-to-r from-[#7C3AED] via-[#F97316] to-[#FACC15] text-[#1B0B26] font-bold hover:shadow-[0_0_22px_rgba(249,115,22,0.5)]',
  },
  christmas: {
    plain:
      'bg-[#08251B]/78 backdrop-blur-md border border-emerald-300/30 text-emerald-50 hover:border-red-300/60 hover:bg-[#0C3527]/88',
    gradient:
      'bg-gradient-to-r from-[#166534] via-[#DC2626] to-[#F59E0B] text-white font-bold hover:shadow-[0_0_22px_rgba(220,38,38,0.45)]',
  },
};

/**
 * Bảng màu cho phần "chrome" (trang liên hệ, modal liên hệ) của các giao diện lễ hội.
 * Trả về null với giao diện không có bảng màu riêng -> component dùng style mặc định.
 */
export interface ThemeAccent {
  pageBackground: string;
  headerBg: string;
  headerBorder: string;
  card: { background: string; border: string; backdropFilter: string };
  text: string;
  subText: string;
  mutedText: string;
  iconBg: string;
  iconColor: string;
  headingGradient: string;
  cta: { background: string; color: string; boxShadow: string };
  ghost: { background: string; border: string; color: string };
  divider: string;
  accentColor: string;
}

const MIDAUTUMN_ACCENT: ThemeAccent = {
  pageBackground: 'linear-gradient(165deg,#070B1E 0%,#141C3A 42%,#2E1A47 100%)',
  headerBg: 'rgba(10,15,38,0.92)',
  headerBorder: 'rgba(253,230,138,0.25)',
  card: {
    background: 'rgba(24,33,72,0.6)',
    border: '1px solid rgba(253,230,138,0.22)',
    backdropFilter: 'blur(8px)',
  },
  text: '#FDE68A',
  subText: 'rgba(253,230,138,0.72)',
  mutedText: 'rgba(253,230,138,0.45)',
  iconBg: 'linear-gradient(135deg,#B45309,#F59E0B)',
  iconColor: '#2A1704',
  headingGradient: 'linear-gradient(135deg,#FDE68A,#FFF7D6,#F59E0B)',
  cta: {
    background: 'linear-gradient(135deg,#B45309 0%,#F59E0B 50%,#FDE68A 100%)',
    color: '#2A1704',
    boxShadow: '0 0 24px rgba(245,158,11,0.4)',
  },
  ghost: {
    background: 'rgba(245,158,11,0.12)',
    border: '1px solid rgba(253,230,138,0.35)',
    color: '#FDE68A',
  },
  divider: '1px solid rgba(253,230,138,0.2)',
  accentColor: '#F59E0B',
};

const HALLOWEEN_ACCENT: ThemeAccent = {
  pageBackground: 'linear-gradient(165deg,#0A0511 0%,#1B0B2B 45%,#2B1206 100%)',
  headerBg: 'rgba(12,6,22,0.92)',
  headerBorder: 'rgba(249,115,22,0.3)',
  card: {
    background: 'rgba(32,16,48,0.62)',
    border: '1px solid rgba(249,115,22,0.25)',
    backdropFilter: 'blur(8px)',
  },
  text: '#FDBA74',
  subText: 'rgba(253,186,116,0.75)',
  mutedText: 'rgba(253,186,116,0.45)',
  iconBg: 'linear-gradient(135deg,#7C3AED,#F97316)',
  iconColor: '#1B0B26',
  headingGradient: 'linear-gradient(135deg,#FACC15,#F97316,#A855F7)',
  cta: {
    background: 'linear-gradient(135deg,#7C3AED 0%,#F97316 55%,#FACC15 100%)',
    color: '#1B0B26',
    boxShadow: '0 0 24px rgba(249,115,22,0.45)',
  },
  ghost: {
    background: 'rgba(249,115,22,0.12)',
    border: '1px solid rgba(249,115,22,0.35)',
    color: '#FDBA74',
  },
  divider: '1px solid rgba(249,115,22,0.22)',
  accentColor: '#F97316',
};

const CHRISTMAS_ACCENT_DARK: ThemeAccent = {
  pageBackground: 'linear-gradient(165deg,#03150F 0%,#08251B 45%,#0B1A2E 100%)',
  headerBg: 'rgba(5,26,19,0.92)',
  headerBorder: 'rgba(134,239,172,0.25)',
  card: {
    background: 'rgba(9,45,33,0.6)',
    border: '1px solid rgba(134,239,172,0.22)',
    backdropFilter: 'blur(8px)',
  },
  text: '#DCFCE7',
  subText: 'rgba(220,252,231,0.72)',
  mutedText: 'rgba(220,252,231,0.45)',
  iconBg: 'linear-gradient(135deg,#166534,#DC2626)',
  iconColor: '#FFFFFF',
  headingGradient: 'linear-gradient(135deg,#FFFFFF,#BBF7D0,#FCA5A5)',
  cta: {
    background: 'linear-gradient(135deg,#166534 0%,#DC2626 60%,#F59E0B 100%)',
    color: '#FFFFFF',
    boxShadow: '0 0 24px rgba(220,38,38,0.4)',
  },
  ghost: {
    background: 'rgba(134,239,172,0.12)',
    border: '1px solid rgba(134,239,172,0.35)',
    color: '#DCFCE7',
  },
  divider: '1px solid rgba(134,239,172,0.2)',
  accentColor: '#DC2626',
};

const CHRISTMAS_ACCENT_LIGHT: ThemeAccent = {
  pageBackground: 'linear-gradient(165deg,#F8FBFF 0%,#E6F1EC 45%,#F3E7E7 100%)',
  headerBg: 'rgba(255,255,255,0.9)',
  headerBorder: 'rgba(22,101,52,0.18)',
  card: {
    background: 'rgba(255,255,255,0.86)',
    border: '1px solid rgba(22,101,52,0.16)',
    backdropFilter: 'blur(8px)',
  },
  text: '#14532D',
  subText: 'rgba(20,83,45,0.75)',
  mutedText: 'rgba(20,83,45,0.45)',
  iconBg: 'linear-gradient(135deg,#166534,#DC2626)',
  iconColor: '#FFFFFF',
  headingGradient: 'linear-gradient(135deg,#166534,#DC2626)',
  cta: {
    background: 'linear-gradient(135deg,#166534 0%,#DC2626 60%,#F59E0B 100%)',
    color: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(220,38,38,0.25)',
  },
  ghost: {
    background: 'rgba(22,101,52,0.08)',
    border: '1px solid rgba(22,101,52,0.25)',
    color: '#14532D',
  },
  divider: '1px solid rgba(22,101,52,0.16)',
  accentColor: '#DC2626',
};

export function getThemeAccent(theme: string, dark = true): ThemeAccent | null {
  switch (normalizeVisualTheme(theme)) {
    case 'midautumn':
      return MIDAUTUMN_ACCENT;
    case 'halloween':
      return HALLOWEEN_ACCENT;
    case 'christmas':
      return dark ? CHRISTMAS_ACCENT_DARK : CHRISTMAS_ACCENT_LIGHT;
    default:
      return null;
  }
}
